import type { VideoListItem, VideoStage } from '@ayub/shared'
import { VIDEO_STAGE_LABELS, formatNumber } from '@ayub/shared'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { ApiError } from '@/lib/api'
import { cn } from '@/lib/utils'
import { VideoKanbanCard } from './VideoKanbanCard'
import { VideoKanbanColumnEmptyState } from './EmptyStates'
import { VIDEO_STAGE_META } from './stageMeta'

interface VideoKanbanColumnProps {
  stage: VideoStage
  items: VideoListItem[]
  total: number
  isLoading: boolean
  isFetching: boolean
  error: Error | null
  hasMore: boolean
  celebratory?: boolean
  onLoadMore: () => void
  onCardClick: (id: number) => void
}

/**
 * Kanban ustuni — `docs/04-ui-ux.md` "Kanban ustuni": kengligi desktop 300px / mobil 85vw
 * (scroll-snap), sarlavha yopishqoq, ichida vertikal skroll, 50 tadan keyin "yana yuklash".
 */
export function VideoKanbanColumn({
  stage,
  items,
  total,
  isLoading,
  isFetching,
  error,
  hasMore,
  celebratory,
  onLoadMore,
  onCardClick,
}: VideoKanbanColumnProps) {
  const meta = VIDEO_STAGE_META[stage]

  return (
    <div className="flex w-[85vw] shrink-0 snap-start flex-col sm:w-[300px] sm:snap-align-none">
      <div className="sticky top-0 z-[1] flex items-center gap-2 rounded-t-card border border-b-0 border-app-border bg-app-card px-3 py-2">
        <span className={cn('size-2 shrink-0 rounded-full', meta.dotClass)} aria-hidden="true" />
        <h3 className="text-sm font-semibold text-app-text uppercase">
          {VIDEO_STAGE_LABELS[stage]}
        </h3>
        <span className="tabular ml-auto text-xs font-medium text-app-hint">
          {isLoading ? '…' : formatNumber(total)}
        </span>
      </div>

      {/* `aria-busy` — skrinrider uchun: ustun yuklanayotganda jim turmasin. */}
      <div
        className="flex min-h-[200px] flex-1 flex-col gap-2 rounded-b-card border border-app-border bg-app-bg p-2"
        aria-busy={isLoading}
      >
        {isLoading && <ColumnSkeleton />}

        {!isLoading && error && items.length === 0 && <ColumnError error={error} />}

        {!isLoading && !error && items.length === 0 && (
          <VideoKanbanColumnEmptyState celebratory={celebratory} />
        )}

        {items.map((video) => (
          <VideoKanbanCard key={video.id} video={video} onClick={onCardClick} />
        ))}

        {!isLoading && items.length > 0 && hasMore && (
          <Button
            variant="secondary"
            size="sm"
            onClick={onLoadMore}
            disabled={isFetching}
            className="mt-1"
          >
            {isFetching ? 'Yuklanmoqda...' : 'Yana yuklash'}
          </Button>
        )}
      </div>
    </div>
  )
}

function ColumnSkeleton() {
  return (
    <>
      {Array.from({ length: 3 }, (_, index) => (
        <Skeleton key={index} className="h-[88px]" />
      ))}
    </>
  )
}

function ColumnError({ error }: { error: Error }) {
  const isServerError = error instanceof ApiError
  return (
    <div className="rounded-card border border-urgency-overdue/30 bg-urgency-overdue/10 p-3 text-xs text-urgency-overdue-text">
      {isServerError ? error.message : "Aloqa yo'q. Qayta urinib ko'ring."}
    </div>
  )
}
